import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "children", "content", "defaultOpen", "open", "onOpen", "onClose", "trigger", "animation"];
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
import React, { useState, useEffect, useRef } from 'react';
import { Modal as SemanticUiModal, TransitionablePortal } from 'semantic-ui-react';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { DEFAULT_MODAL_ANIMATION } from './Modal.constants';
import { ModalAnimationPropType } from './Modal.types';
import { addFadeInToDocument, removeFadeInToDocument } from './Modal.utils';
import { classNames } from '../../../../utils/classname';
import useSemanticUiProps from '../../hooks/use-semantic-ui-props';
import { createComponent } from '../../services/components';
import { ChildrenPropType } from '../../types/component';
import './Modal.css';
var defaultProps = {
  className: '',
  children: null,
  content: null,
  open: undefined,
  defaultOpen: false,
  trigger: null,
  animation: DEFAULT_MODAL_ANIMATION,
  onOpen: null,
  onClose: null
};
export default function Modal(_ref) {
  var className = _ref.className,
    children = _ref.children,
    content = _ref.content,
    defaultOpen = _ref.defaultOpen,
    openProp = _ref.open,
    onOpenProp = _ref.onOpen,
    onCloseProp = _ref.onClose,
    trigger = _ref.trigger,
    animation = _ref.animation,
    rest = _objectWithoutProperties(_ref, _excluded);
  var mounted = useRef(false);
  var isControlled = !_isNil(openProp);
  var _useState = useState(defaultOpen),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var finalOpen = isControlled ? openProp : open;
  useEffect(function () {
    if (finalOpen) {
      addFadeInToDocument();
    }
    mounted.current = true;
    return function () {
      removeFadeInToDocument();
    };
  }, []);
  useEffect(function () {
    if (mounted.current) {
      if (!finalOpen) {
        removeFadeInToDocument();
      }
      if (finalOpen) {
        addFadeInToDocument();
      }
    }
  }, [finalOpen]);
  var _useSemanticUiProps = useSemanticUiProps(),
    getModalProps = _useSemanticUiProps.getModalProps,
    getTransitionablePortalProps = _useSemanticUiProps.getTransitionablePortalProps;
  var finalAnimation = _isEmpty(animation) ? null : _objectSpread(_objectSpread({}, DEFAULT_MODAL_ANIMATION), animation);
  var finalClassName = classNames(_defineProperty({
    'animated-modal': !_isEmpty(finalAnimation),
    'fisherman-modal': true
  }, className, className));
  function showModal() {
    addFadeInToDocument();
    setOpen(true);
    if (onOpenProp) {
      onOpenProp();
    }
  }
  function closeModal() {
    removeFadeInToDocument();
    setOpen(false);
    if (onCloseProp) {
      onCloseProp();
    }
  }
  if (!_isEmpty(finalAnimation)) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, isControlled ? trigger : createComponent(trigger, {
      onClick: showModal
    }), /*#__PURE__*/React.createElement(TransitionablePortal, _extends({
      open: finalOpen,
      transition: finalAnimation,
      onOpen: showModal,
      onClose: closeModal
    }, getTransitionablePortalProps(rest)), /*#__PURE__*/React.createElement(SemanticUiModal, _extends({
      className: finalClassName,
      content: content,
      open: true,
      closeOnEscape: true,
      closeOnDimmerClick: false,
      onOpen: showModal,
      onClose: closeModal
    }, getModalProps(rest)), children)));
  }
  return /*#__PURE__*/React.createElement(SemanticUiModal, _extends({
    className: finalClassName,
    content: content,
    open: openProp,
    onOpen: showModal,
    onClose: closeModal,
    trigger: trigger,
    closeOnEscape: true,
    closeOnDimmerClick: false
  }, getModalProps(rest)), children);
}
Modal.defaultProps = defaultProps;
Modal.Actions = SemanticUiModal.Actions;
Modal.Content = SemanticUiModal.Content;
Modal.Description = SemanticUiModal.Description;
Modal.Header = SemanticUiModal.Header;
Modal.Dimmer = SemanticUiModal.Dimmer;